import { amountToString, newAmount } from '@orus.eu/amount'
import { type Result, success } from '@orus.eu/result'
import type { ParameterlessTranslationKey } from '@orus.eu/translations'

import {
  type DimensionOptions,
  type DimensionValidationProblem,
  dimensionValidationFailure,
} from './abstract-dimension.ts'
import { AbstractNumberDimension, AbstractNumberDimensionWithDefaultOption } from './abstract-number-dimension.ts'

export type NumbersDimensionOptions<
  NAME extends string,
  CURRENT_VALUES extends readonly number[],
  SUPPORTED_VALUES extends readonly number[],
> = DimensionOptions<NAME, CURRENT_VALUES[number]> & {
  values: CURRENT_VALUES
  supportedValues?: SUPPORTED_VALUES
  valuesAreAmount: boolean
}

export class NumbersEnumDimension<
  NAME extends string,
  CURRENT_VALUES extends readonly number[],
  SUPPORTED_VALUES extends readonly number[] = CURRENT_VALUES,
> extends AbstractNumberDimension<NAME, CURRENT_VALUES[number]> {
  readonly type = 'numbers-enum'
  readonly values: CURRENT_VALUES
  readonly supportedValues: SUPPORTED_VALUES | undefined
  readonly valuesAreAmount: boolean

  constructor(options: NumbersDimensionOptions<NAME, CURRENT_VALUES, SUPPORTED_VALUES>) {
    super(options)
    this.values = options.values
    this.supportedValues = options.supportedValues ?? undefined
    this.valuesAreAmount = options.valuesAreAmount
  }

  validateNumber(value: number): Result<CURRENT_VALUES[number], DimensionValidationProblem> {
    if ((this.supportedValues ?? this.values).includes(value)) return success(value)
    return dimensionValidationFailure(`Field ${this.name} is a number but has an invalid value`)
  }

  override formatToString(value: CURRENT_VALUES[number]): string | undefined {
    if (this.valuesAreAmount) {
      return amountToString(newAmount(value), { addCurrency: true })
    }

    return value.toString()
  }
}

/**
 * A variant of the numbers enum dimension that allows the "default" string as a valid value.
 * See {@link AbstractNumberDimensionWithDefaultOption} for more details.
 */
export class NumbersEnumDimensionWithDefaultOption<
  NAME extends string,
  CURRENT_VALUES extends readonly number[],
  SUPPORTED_VALUES extends readonly number[] = CURRENT_VALUES,
> extends AbstractNumberDimensionWithDefaultOption<NAME, CURRENT_VALUES[number]> {
  readonly type = 'numbers-enum-with-default-option'
  readonly values: ['default', ...CURRENT_VALUES]
  readonly supportedValues: ['default', ...SUPPORTED_VALUES] | undefined
  readonly defaultValueTranslationKey: ParameterlessTranslationKey

  constructor(
    options: NumbersDimensionOptions<NAME, CURRENT_VALUES, SUPPORTED_VALUES> & {
      defaultValueTranslationKey: ParameterlessTranslationKey
    },
  ) {
    super(options)
    this.values = ['default', ...options.values]
    this.supportedValues = options.supportedValues ? ['default', ...options.supportedValues] : undefined
    this.defaultValueTranslationKey = options.defaultValueTranslationKey
  }

  validateNumber(value: number): Result<CURRENT_VALUES[number], DimensionValidationProblem> {
    if ((this.supportedValues ?? this.values).includes(value)) return success(value)
    return dimensionValidationFailure(`Field ${this.name} is a number but has an invalid value`)
  }

  override formatToString(value: CURRENT_VALUES[number]): string | undefined {
    return value.toString()
  }
}
